import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';

// Estilos copiados de Vendas.js (fonte única do padrão visual do sistema)
const fmt = v => 'R$ ' + parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const th = { padding: '10px 12px', background: '#F5F7FA', color: '#9AA3AE', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const td = { padding: '12px 12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };
const filtroInp = { padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" };
const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const cardKpi = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '14px 16px' };
const kpiLabel = { fontSize: '11px', color: '#6B7A8D', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' };
const mono = { fontFamily: "'DM Mono',monospace" };

// Data em ISO a partir das partes LOCAIS. Não usar toISOString(): o servidor é
// UTC e a loja é UTC-3, então depois das 21h o toISOString() já devolve o dia
// seguinte — a venda nascia com data de amanhã e sumia do filtro do mês.
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hojeISO = () => toISO(new Date());
const primeiroDiaMes = () => { const h = new Date(); return toISO(new Date(h.getFullYear(), h.getMonth(), 1)); };
const ultimoDiaMes = () => { const h = new Date(); return toISO(new Date(h.getFullYear(), h.getMonth() + 1, 0)); };
const dataBR = (v) => v ? new Date(String(v).split('T')[0] + 'T00:00').toLocaleDateString('pt-BR') : '—';

const PERC_PADRAO = '1.00';

const formVazio = () => ({
  cliente_nome: '', cliente_cpf: '', cliente_telefone: '',
  valor_carta: '', prazo_meses: '', com_seguro: false,
  vendedor_id: '', data_venda: hojeISO(), comissao_percentual: PERC_PADRAO, observacoes: '',
});

const badge = (bg, cor) => ({ padding: '3px 9px', background: bg, color: cor, borderRadius: '20px', fontSize: '10.5px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', display: 'inline-block' });
const BADGE_STATUS = {
  pendente:   { estilo: badge('#FDF3E3', '#B06000'), texto: 'Pendente' },
  confirmado: { estilo: badge('#EAF5EE', '#2D7D4F'), texto: 'Confirmado' },
  cancelado:  { estilo: badge('#FDECEA', '#C0392B'), texto: 'Cancelado' },
};

export default function Consorcios() {
  const { usuario } = useAuth();
  const somenteLeitura = isVisualizacao(usuario); // visualização vê como admin, sem ações
  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente' || somenteLeitura;
  const podeEditar = isAdmin && !somenteLeitura;

  const [consorcios, setConsorcios] = useState([]);
  const [totais, setTotais] = useState({ quantidade: 0, qtd_pendentes: 0, qtd_confirmados: 0, valor_cartas: 0, comissoes_confirmadas: 0, comissoes_pendentes: 0 });
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(ultimoDiaMes());
  const [vendedorFiltro, setVendedorFiltro] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(formVazio());
  const [salvando, setSalvando] = useState(false);
  const [ocupado, setOcupado] = useState(null); // id em operação (checklist/confirmar)

  const carregar = async (ini = inicio, f = fim, vend = vendedorFiltro) => {
    setLoading(true);
    try {
      const params = {};
      if (ini) params.inicio = ini;
      if (f) params.fim = f;
      if (vend) params.vendedor_id = vend;
      const r = await api.get('/consorcios', { params });
      setConsorcios(r.data.consorcios || []);
      if (r.data.totais) setTotais(r.data.totais);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    carregar();
    api.get('/usuarios').then(r => setUsuarios((r.data || []).filter(u => u.ativo !== false))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setV = (campo, val) => setForm(f => ({ ...f, [campo]: val }));

  // Comissão R$ é sempre derivada — read-only na tela e recalculada no backend.
  const comissaoCalculada = (parseFloat(form.valor_carta || 0) * parseFloat(form.comissao_percentual || 0)) / 100;

  const abrirNova = () => {
    setEditandoId(null);
    setForm({ ...formVazio(), vendedor_id: String(usuario?.id || '') });
    setModalAberto(true);
  };

  const abrirEditar = (c) => {
    setEditandoId(c.id);
    setForm({
      cliente_nome: c.cliente_nome || '',
      cliente_cpf: c.cliente_cpf || '',
      cliente_telefone: c.cliente_telefone || '',
      valor_carta: c.valor_carta || '',
      prazo_meses: c.prazo_meses || '',
      com_seguro: !!c.com_seguro,
      vendedor_id: c.vendedor_id ? String(c.vendedor_id) : '',
      data_venda: c.data_venda ? String(c.data_venda).split('T')[0] : hojeISO(),
      comissao_percentual: c.comissao_percentual != null ? String(c.comissao_percentual) : PERC_PADRAO,
      observacoes: c.observacoes || '',
    });
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setEditandoId(null); setForm(formVazio()); };

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.cliente_nome.trim()) { alert('Informe o nome do cliente'); return; }
    if (!(parseFloat(form.valor_carta) > 0)) { alert('Informe o valor da carta'); return; }
    if (!(parseInt(form.prazo_meses, 10) > 0)) { alert('Informe o prazo em meses'); return; }
    if (!form.vendedor_id) { alert('Selecione o vendedor'); return; }
    setSalvando(true);
    try {
      if (editandoId) {
        const r = await api.put('/consorcios/' + editandoId, form);
        if (r.data?.aviso) alert(r.data.aviso);
      } else {
        await api.post('/consorcios', form);
      }
      fecharModal();
      carregar();
    } catch (err) { alert(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  // Marca/desmarca uma das duas travas. Só admin (backend também barra).
  const marcar = async (c, campo, valor) => {
    setOcupado(c.id);
    try {
      await api.put('/consorcios/' + c.id + '/checklist', { [campo]: valor });
      carregar();
    } catch (err) { alert(err.response?.data?.error || 'Erro ao atualizar'); }
    finally { setOcupado(null); }
  };

  const confirmar = async (c) => {
    if (!window.confirm(
      'Confirmar a venda de consórcio de ' + c.cliente_nome + '?\n\n' +
      'Isso gera a comissão de ' + fmt(c.comissao_valor) + ' para ' + (c.vendedor_nome || '—') +
      ' no Financeiro, como A Pagar com vencimento em D+30 da data da venda.'
    )) return;
    setOcupado(c.id);
    try {
      const r = await api.post('/consorcios/' + c.id + '/confirmar');
      if (r.data?.aviso) alert(r.data.aviso);
      carregar();
    } catch (err) { alert(err.response?.data?.error || 'Erro ao confirmar'); }
    finally { setOcupado(null); }
  };

  const cancelar = async (c) => {
    const msg = c.status === 'confirmado'
      ? 'Esta venda já está confirmada — o lançamento de comissão de ' + fmt(c.comissao_valor) + ' será cancelado no Financeiro.'
      : 'Esta venda ainda está pendente — nenhuma comissão foi gerada, então nada muda no Financeiro.';
    if (!window.confirm('Cancelar o consórcio de ' + c.cliente_nome + ' (' + fmt(c.valor_carta) + ')?\n\n' + msg)) return;
    try {
      await api.delete('/consorcios/' + c.id);
      carregar();
    } catch (err) { alert(err.response?.data?.error || 'Erro ao cancelar consórcio'); }
  };

  // Checkbox das duas travas. Vendedor vê desabilitado, só para acompanhar.
  const CheckTrava = ({ c, campo, texto }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: c[campo] ? '#2D7D4F' : '#6B7A8D', cursor: podeEditar ? 'pointer' : 'default', whiteSpace: 'nowrap', fontWeight: c[campo] ? '600' : '400' }}>
      <input type='checkbox' checked={!!c[campo]}
        disabled={!podeEditar || ocupado === c.id}
        onChange={e => marcar(c, campo, e.target.checked)}
        style={{ width: '14px', height: '14px', cursor: podeEditar ? 'pointer' : 'default', accentColor: '#2D7D4F', margin: 0 }} />
      {texto}
    </label>
  );

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>
          📋 Consórcios <span style={{ fontSize: '14px', color: '#6B7A8D', fontWeight: '400' }}>({consorcios.length})</span>
        </div>
        {!somenteLeitura && <button onClick={abrirNova}
          style={{ padding: '9px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
          + Nova Venda de Consórcio
        </button>}
      </div>

      {/* Filtros */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '12px 16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: '#9AA3AE', fontSize: '12px', fontWeight: '600' }}>Período:</span>
          <input type='date' value={inicio} onChange={e => setInicio(e.target.value)} style={filtroInp} />
          <span style={{ color: '#9AA3AE', fontSize: '13px' }}>até</span>
          <input type='date' value={fim} onChange={e => setFim(e.target.value)} style={filtroInp} />
          <button onClick={() => carregar(inicio, fim, vendedorFiltro)}
            style={{ padding: '8px 14px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', fontSize: '13px' }}>
            Buscar
          </button>
          <button onClick={() => { setInicio(''); setFim(''); setVendedorFiltro(''); carregar('', '', ''); }}
            style={{ padding: '8px 14px', background: '#F4F6F9', border: 'none', color: '#6B7A8D', borderRadius: '7px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontSize: '13px' }}>
            Todos
          </button>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
              <span style={{ color: '#9AA3AE', fontSize: '12px', fontWeight: '600' }}>Vendedor:</span>
              <select value={vendedorFiltro} onChange={e => { setVendedorFiltro(e.target.value); carregar(inicio, fim, e.target.value); }}
                style={{ ...filtroInp, minWidth: '160px', cursor: 'pointer' }}>
                <option value=''>Todos os vendedores</option>
                {usuarios.map(u => <option key={u.id} value={String(u.id)}>{u.nome}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Totais — comissão confirmada (já é despesa) separada da pendente (potencial) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '14px' }}>
        <div style={cardKpi}>
          <div style={kpiLabel}>Consórcios no período</div>
          <div style={{ ...mono, fontSize: '28px', fontWeight: '700', color: '#1E5FA5' }}>{totais.quantidade}</div>
          <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '2px' }}>
            {totais.qtd_confirmados} confirmado(s) · {totais.qtd_pendentes} pendente(s)
          </div>
        </div>
        <div style={cardKpi}>
          <div style={kpiLabel}>Valor total das cartas</div>
          <div style={{ ...mono, fontSize: '20px', fontWeight: '700', color: '#1A3A5C' }}>{fmt(totais.valor_cartas)}</div>
        </div>
        <div style={cardKpi}>
          <div style={kpiLabel}>Comissões confirmadas</div>
          <div style={{ ...mono, fontSize: '20px', fontWeight: '700', color: '#2D7D4F' }}>{fmt(totais.comissoes_confirmadas)}</div>
          <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '2px' }}>já lançadas no Financeiro</div>
        </div>
        <div style={cardKpi}>
          <div style={kpiLabel}>Comissões pendentes</div>
          <div style={{ ...mono, fontSize: '20px', fontWeight: '700', color: '#B06000' }}>{fmt(totais.comissoes_pendentes)}</div>
          <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '2px' }}>potencial — não lançado</div>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px', overflowX: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Carregando...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Data', 'Cliente', 'Valor da carta', 'Prazo', 'Seguro', 'Vendedor', 'Comissão', 'Status', 'Confirmação', podeEditar ? '' : null]
                  .filter(h => h !== null)
                  .map((h, i) => <th key={i} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {consorcios.length === 0 && (
                <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhum consórcio no período</td></tr>
              )}
              {consorcios.map(c => {
                const cancelado = c.status === 'cancelado';
                const pendente = c.status === 'pendente';
                const podeConfirmar = pendente && c.pagou_primeira_parcela && c.assinou_contrato;
                const st = BADGE_STATUS[c.status] || BADGE_STATUS.pendente;
                return (
                  <tr key={c.id} style={cancelado ? { opacity: 0.55 } : undefined}>
                    <td style={{ ...td, ...mono, color: '#6B7A8D', fontSize: '12px' }}>{dataBR(c.data_venda)}</td>
                    <td style={{ ...td, fontWeight: '500' }}>
                      {c.cliente_nome}
                      {c.cliente_telefone && <div style={{ fontSize: '11px', color: '#9AA3AE' }}>{c.cliente_telefone}</div>}
                    </td>
                    <td style={{ ...td, ...mono, fontWeight: '700', color: '#1A3A5C', fontSize: '12.5px' }}>{fmt(c.valor_carta)}</td>
                    <td style={{ ...td, ...mono, color: '#6B7A8D', fontSize: '12px' }}>{c.prazo_meses}x</td>
                    <td style={td}>
                      <span style={c.com_seguro ? badge('#EAF5EE', '#2D7D4F') : badge('#F4F6F9', '#9AA3AE')}>
                        {c.com_seguro ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#6B7A8D' }}>
                      {c.vendedor_nome || '—'}
                      {c.registrado_por_nome && c.registrado_por !== c.vendedor_id && (
                        <div style={{ fontSize: '10px', color: '#9AA3AE' }}>reg. por {c.registrado_por_nome}</div>
                      )}
                    </td>
                    <td style={{ ...td, ...mono, fontWeight: '700', fontSize: '12.5px', color: c.status === 'confirmado' ? '#2D7D4F' : '#9AA3AE' }}>
                      <div>{fmt(c.comissao_valor)}</div>
                      <div style={{ fontSize: '10px', color: '#9AA3AE', fontWeight: '400' }}>
                        {parseFloat(c.comissao_percentual || 0)}%
                        {c.lancamento_status === 'pago' && <span style={{ color: '#2D7D4F', fontWeight: '600' }}> · ✓ paga</span>}
                        {c.lancamento_status === 'pendente' && c.comissao_vencimento && <span> · vence {dataBR(c.comissao_vencimento)}</span>}
                        {!c.lancamento_status && pendente && <span> · não lançada</span>}
                      </div>
                    </td>
                    <td style={td}><span style={st.estilo}>{st.texto}</span></td>
                    <td style={td}>
                      {cancelado ? (
                        <span style={{ fontSize: '11px', color: '#9AA3AE' }}>—</span>
                      ) : c.status === 'confirmado' ? (
                        <div style={{ fontSize: '11px', color: '#2D7D4F', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          ✓ 1ª parcela e contrato
                          {c.confirmado_por_nome && (
                            <div style={{ fontSize: '10px', color: '#9AA3AE', fontWeight: '400' }}>
                              por {c.confirmado_por_nome}{c.confirmado_em ? ' · ' + dataBR(c.confirmado_em) : ''}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <CheckTrava c={c} campo='pagou_primeira_parcela' texto='1ª parcela paga' />
                          <CheckTrava c={c} campo='assinou_contrato' texto='Contrato assinado' />
                          {podeEditar && podeConfirmar && (
                            <button onClick={() => confirmar(c)} disabled={ocupado === c.id}
                              style={{ marginTop: '3px', padding: '4px 10px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                              {ocupado === c.id ? '...' : 'Confirmar'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    {podeEditar && (
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button onClick={() => abrirEditar(c)}
                          style={{ padding: '4px 10px', marginRight: '4px', background: 'transparent', border: '1px solid #B06000', color: '#B06000', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>
                          Editar
                        </button>
                        {!cancelado && (
                          <button onClick={() => cancelar(c)}
                            style={{ padding: '4px 10px', background: '#FDECEA', border: 'none', color: '#C0392B', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>
                            Cancelar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Nova / Editar */}
      {modalAberto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '620px', maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>
                {editandoId ? 'Editar Consórcio #' + editandoId : 'Nova Venda de Consórcio'}
              </h3>
              <button onClick={fecharModal} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={salvar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div style={{ marginBottom: '12px', gridColumn: '1 / -1' }}>
                  <label style={lbl}>Cliente *</label>
                  <input value={form.cliente_nome} onChange={e => setV('cliente_nome', e.target.value)} style={inpStyle} placeholder='Nome completo' />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>CPF</label>
                  <input value={form.cliente_cpf} onChange={e => setV('cliente_cpf', e.target.value)} style={inpStyle} placeholder='000.000.000-00' />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Telefone</label>
                  <input value={form.cliente_telefone} onChange={e => setV('cliente_telefone', e.target.value)} style={inpStyle} placeholder='(00) 00000-0000' />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Valor da carta *</label>
                  <input type='number' step='0.01' min='0' value={form.valor_carta} onChange={e => setV('valor_carta', e.target.value)}
                    style={{ ...inpStyle, fontWeight: '700', color: '#1A3A5C' }} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Prazo (meses) *</label>
                  <input type='number' min='1' step='1' value={form.prazo_meses} onChange={e => setV('prazo_meses', e.target.value)} style={inpStyle} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Data da venda</label>
                  <input type='date' value={form.data_venda} onChange={e => setV('data_venda', e.target.value)} style={inpStyle} />
                </div>
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 0', fontSize: '13px', color: '#1C2533' }}>
                    <input type='checkbox' checked={!!form.com_seguro} onChange={e => setV('com_seguro', e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1A3A5C' }} />
                    Com seguro?
                  </label>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Vendedor *</label>
                  <select value={form.vendedor_id} onChange={e => setV('vendedor_id', e.target.value)} style={inpStyle}>
                    <option value=''>Selecione</option>
                    {usuarios.map(u => <option key={u.id} value={String(u.id)}>{u.nome}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Comissão % {!isAdmin && <span style={{ textTransform: 'none', fontWeight: '400', color: '#9AA3AE' }}>(só admin edita)</span>}</label>
                  <input type='number' step='0.01' min='0' value={form.comissao_percentual}
                    onChange={e => setV('comissao_percentual', e.target.value)}
                    disabled={!isAdmin} readOnly={!isAdmin}
                    style={{ ...inpStyle, background: isAdmin ? '#F4F6F9' : '#ECEFF3', color: isAdmin ? '#1C2533' : '#6B7A8D', cursor: isAdmin ? 'text' : 'not-allowed' }} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Comissão R$ (calculada)</label>
                  <input readOnly value={fmt(comissaoCalculada)}
                    style={{ ...inpStyle, ...mono, background: '#EAF5EE', border: '0.5px solid rgba(45,125,79,0.25)', color: '#2D7D4F', fontWeight: '700', cursor: 'default' }} />
                </div>
                <div style={{ marginBottom: '12px', gridColumn: '1 / -1' }}>
                  <label style={lbl}>Observações</label>
                  <input value={form.observacoes} onChange={e => setV('observacoes', e.target.value)} style={inpStyle} />
                </div>
              </div>

              {!editandoId && (
                <div style={{ background: '#FDF3E3', border: '0.5px solid rgba(176,96,0,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#6B4A10' }}>
                  A venda entra como <strong>pendente</strong> e <strong>não gera comissão agora</strong>.
                  A comissão de <strong>{fmt(comissaoCalculada)}</strong> só é lançada no Financeiro depois que o
                  administrador marcar <strong>1ª parcela paga</strong> e <strong>contrato assinado</strong> e clicar em <strong>Confirmar</strong>.
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button type='button' onClick={fecharModal}
                  style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  Cancelar
                </button>
                <button type='submit' disabled={salvando}
                  style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  {salvando ? 'Salvando...' : (editandoId ? 'Salvar alterações' : 'Registrar consórcio')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
